import { LitElement, html, css } from "https://cdn.jsdelivr.net/npm/lit-element@4.1.1/+esm";
import { getSharedStyles, preloadSharedStyles } from "/modules/styleSheet.js";

// Pre-load shared stylesheets immediately when this module loads
await preloadSharedStyles();

/**
 * Base class for all the components, with the same CSS.
 */
export class ShadowDOMElement extends LitElement {
	static get styles() {
		return getSharedStyles();
  	}
	
	#children = [];
	#parent = null;
	
	isTransient (){
		return true;
	}
	
	get children (){
		return this.#children;
	}
	
	get parent (){
		return this.#parent;
	}
	
	#dockParent (){
		if (this.#parent || this.isTransient ())
			return;
		
		const move = (node) => node instanceof ShadowRoot ? node.host : node.parentNode;
		
		const findParent = (node) => {
			for (node = move (node); node && !(node instanceof ShadowDOMElement && !node.isTransient ()); node = move (node));
			return node;
		}
		
		const node = findParent (this);
		if (node && !node.children.includes (node)){
			node.children.push (this);
			this.#parent = node;
		}
	}
	
	#undockParent (){
		if (!this.#parent || this.isTransient ())
			return;
		
		if (this.#parent.children.includes (this)){
			const index = this.#parent.children.indexOf (this);
			if (index >= 0)
				this.#parent.children.splice (index, 1);
			this.#parent = null;
		}
	}
	
	connectedCallback (){
		super.connectedCallback ();
		this.#dockParent ();
	}
	
	disconnectedCallback (){
		super.disconnectedCallback ();
		this.#undockParent (); 
	}
	
	getValues (){
		return {
			"children" : this.children.map ((node) => ({
				"class" : node.constructor.className || (node.tagName ? node.tagName.toLowerCase() : node.constructor.name),
				"id" : node.getAttribute ("id"),
				... node.getValues ()
			}))
		};
	}
	
	setValues (values){
		let children = [... this.#children];
		
		const find = name => children.find (item => {
			const className = item.constructor.className || (item.tagName ? item.tagName.toLowerCase() : item.constructor.name);
			return className === name || item.constructor.name === name;
		});
		const filter = node => children.filter (item => item != node);
		
		if (values && values.children){
			values.children.forEach (item => {
				const node = find (item ["class"]);
				if (node){
					node.setValues (item);
					children = filter (node);
				}
			});
		}
	}
	
	static #findComponentsFromNode (parent){
		let children = [];
		const findComponents = (node) => {
			[{"root" : node, "query" : ":scope > *"}, {"root" : node.shadowRoot, "query" : ":host > *"}].forEach (item => {
				if (item.root)
					item.root.querySelectorAll (item.query).forEach (child => {
						if (child instanceof ShadowDOMElement && !child.isTransient ()){
							if (!children.includes (child))
								children.push (child);
						} else
							findComponents (child);
					});
			});
		}
		findComponents (parent);
		return children;
	}
	
	static getValuesFromNode (parent){
		if (parent instanceof ShadowDOMElement && !parent.isTransient ())
			return parent.getValues ();
		
		const children = ShadowDOMElement.#findComponentsFromNode (parent);		
		
		return {
			"children" : children.map ((node) => ({
				"class" : node.constructor.className || (node.tagName ? node.tagName.toLowerCase() : node.constructor.name),
				"id" : node.getAttribute ("id"),
				... node.getValues ()	
			}))
		};
	}
	
	static setValuesFromNode (values, parent){
		let children = ShadowDOMElement.#findComponentsFromNode (parent);
		
		const find = name => children.find (item => {
			const className = item.constructor.className || (item.tagName ? item.tagName.toLowerCase() : item.constructor.name);
			return className === name || item.constructor.name === name;
		});
		const filter = node => children.filter (item => item != node);
		
		if (values && values.children){
			values.children.forEach (item => {
				const node = find (item ["class"]);
				if (node){
					node.setValues (item);
					children = filter (node);
				}
			});
		} 
	}
}

if (typeof window !== 'undefined')
	window.ShadowDOMElement = ShadowDOMElement;

/**
 * Base class for form fields with a value property.
 * Provides common getValues/setValues implementation.
 */
export class ValueField extends ShadowDOMElement {
	static className = "ValueField";
	
	static properties = {
		label: { type: String },
		placeholder: { type: String },
		value: { type: String },
		name: { type: String },
		idInput: { type: String, attribute: "id-input" },
		required: { type: Boolean },
		autofocusOnLoad: { type: Boolean, attribute: "autofocus-on-load" }
	};
	
	getValues() {
		return { "name": this.name, "value": this.value ?? "", ...super.getValues() };
	}
	
	setValues(values) {
		super.setValues(values);
		if (values.value !== undefined) {
			this.value = values.value;
		}
	}
	
	isTransient() {
		return false;
	}
}

/**
 * Base class for checkbox fields with a checked property.
 * Provides common getValues/setValues implementation and native checkbox observation.
 */
export class CheckboxFieldBase extends ShadowDOMElement {
	static className = "CheckboxFieldBase";
	
	static properties = {
		label: { type: String },
		checked: { 
			type: Boolean, 
			reflect: true,
			converter: {
				fromAttribute: (value) => value === "true",
				toAttribute: (value) => (value ? "true" : "false")
			}
		},
		name: { type: String },
		idInput: { type: String, attribute: "id-input" }
	};
	
	constructor() {
		super();
		this.checked = false;
	}
	
	firstUpdated() {
		this.observeNativeCheckbox();
	}
	
	observeNativeCheckbox() {
		const checkbox = this.renderRoot.querySelector("input[type=checkbox]");
		if (checkbox) {
			const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "checked");
			Object.defineProperty(checkbox, "checked", {
				get: descriptor.get,
				set: function(newValue) {
					descriptor.set.call(this, newValue);
					this.dispatchEvent(new Event("change", { bubbles: true }));
				}
			});
			
			checkbox.addEventListener("change", () => {
				this.checked = checkbox.checked;
				this.dispatchChangeEvent();
			});
		}
	}
	
	handleChange(event) {
		this.checked = event.target.checked;
		this.dispatchChangeEvent();
	}
	
	dispatchChangeEvent() {
		this.dispatchEvent(new CustomEvent("change", { detail: { checked: this.checked }, bubbles: true }));
	}
	
	getValues() {
		return { "name": this.name, "checked": this.checked ?? false, ...super.getValues() };
	}
	
	setValues(values) {
		super.setValues(values);
		if (values.checked !== undefined) {
			this.checked = values.checked;
		}
	}
	
	isTransient() {
		return false;
	}
}

export class TextField extends ValueField {
	static className = "TextField";
	
	static properties = {
		...ValueField.properties,
		disableAutocomplete: { type: Boolean, attribute: "disable-autocomplete" },
		pattern: { type: String }
	};
	
	constructor() {
		super();
		this.disableAutocomplete = true;
	}
	
	render() {
		return html`
			<div class="row align-items-center">
				<label for="${this.idInput}" class="col-sm-3 col-form-label text-end">${this.label}</label>
				<div class="col-sm-9">
					<input
						id="${this.idInput}"
						type="text"
						placeholder="${this.placeholder}"
						value="${this.value}"
						name="${this.name}"
						data-input ?required=${this.required}
						autocomplete="${this.disableAutocomplete ? 'new-password' : 'on'}"
						pattern="${this.pattern}"
						@input="${this.handleInput}"/>
				</div>
			</div>
		`;
	}
	
	handleInput(event) {
		this.value = event.target.value;
	}
}

export class NumberField extends ValueField {
	static className = "NumberField";
	
	static properties = {
		...ValueField.properties,
		disableAutocomplete: { type: Boolean, attribute: "disable-autocomplete" },
		min: { type: Number },
		max: { type: Number },
		maxLength: { type: Number, attribute: "max-length" }
	};
	
	constructor() {
		super();
		this.disableAutocomplete = true;
	}
	
	render (){
		return html`
			<div class="row align-items-center">
				<label for="${this.idInput}" class="col-sm-3 col-form-label text-end">${this.label}</label>
				<div class="col-sm-9">
					<input
						id="${this.idInput}"
						type="text"
						pattern="\\d*"
						placeholder="${this.placeholder}"
						value="${this.value}"
						name="${this.name}"
						data-input ?required=${this.required}
						autocomplete="${this.disableAutocomplete ? 'off' : 'on'}"
						@input="${this.handleInput}"
						min="${this.min}"
						max="${this.max}",
						maxlength="${this.maxLength}"
						oninput="this.value = this.value.replace(/[^0-9]/g, '');"
						inputmode="numeric"
					/>
				</div>
			</div>
		`;
	}
	
	handleInput(event) {
		this.value = event.target.value.replace(/[^0-9]/g, '');
	}
}

export class TitleField extends ShadowDOMElement {
	static className = "TitleField";
	
	static properties = {
		label : {type : String}
	};

	render (){
		return html`
			<h4>${this.label}</h4>
		`;
	}
}

export class SubtitleField extends ShadowDOMElement {
	static className = "SubtitleField";
	
	static properties = {
		label : {type : String}
	};

	render (){
		return html`
			<div class="row align-items-center">
				<div class="col-sm-3"></div>
				<div class="col-sm-9">
					<h5>${this.label}</h5>
				</div>
			</div>
		`;
	}
}

export class OptionComponent extends ShadowDOMElement {
	static className = "OptionComponent";
	
	static properties = {
		value : { type: String },
		label : { type: String },
		selected : { type: Boolean }
	};
	
	render (){
		return html``;
	}
}

export class SelectField extends ShadowDOMElement {
	static className = "SelectField";
	
	static properties = {
		label: { type: String },
		value: { type: String },
		name: { type: String },
		idInput: { type: String, attribute: "id-input" },
		required: { type: Boolean }
	};

	firstUpdated() {
		this.observeNativeSelect();
	}

	observeNativeSelect() {
		const select = this.renderRoot.querySelector("select");
		if (select) {
			const descriptor = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, "value");
			Object.defineProperty(select, "value", {
				get: descriptor.get,
				set: function (newValue) {
					descriptor.set.call(this, newValue);
					this.dispatchEvent(new Event("change", { bubbles: true }));
				}
			});

			select.addEventListener("change", () => {
				this.value = select.value;
				this.dispatchChangeEvent();
			});
		}
	}

	render() {
		const options = Array.from(this.querySelectorAll("option-component"));

		return html`
			<div class="row align-items-center">
				<label for="${this.idInput}" class="col-sm-3 col-form-label text-end">${this.label}</label>
				<div class="col-sm-9">
					<select
						id="${this.idInput}"
						name="${this.name}"
						.value="${this.value}"
						@change="${this.handleChange}"
						?required=${this.required}
					>
						${options.map(option => html`
							<option value="${option.value}" ?selected="${this.value === option.value}">
								${option.label}
							</option>`)}
					</select>
				</div>
			</div>
		`;
	}

	handleChange(event) {
		this.value = event.target.value;
		this.dispatchChangeEvent();
	}

	dispatchChangeEvent() {
		this.dispatchEvent(new CustomEvent("change", { detail: { value: this.value }, bubbles: true }));
	}
	
	getValues (){
		const options = Array.from(this.querySelectorAll ("option-component"));
		const selected = options.find (option => this.value === option.value);
		const optionValue = selected ? selected.label : "";
		return {"name" : this.name, "value" : this.value??"", "optionValue" : optionValue, ... super.getValues ()};
	}
	
	setValues (values){
		super.setValues (values);
		this.value = values.value;
	}
	
	isTransient (){
		return false;
	}
}

export class TextAreaField extends ValueField {
	static className = "TextAreaField";
	
	static properties = {
		...ValueField.properties,
		rows: { type: Number },
		cols: { type: Number }
	};
	
	render (){
		return html`
			<div class="row align-items-center">
				<label for="${this.idInput}" class="col-sm-3 col-form-label text-end">${this.label}</label>
				<div class="col-sm-9">
					<textarea
						id="${this.idInput}"
						name="${this.name}"
						rows="${this.rows}"
						cols="${this.cols}"
						placeholder="${this.placeholder}"
						.value="${this.value ?? ""}"
						@input="${this.handleInput}"
						?required=${this.required}>
					</textarea>
				</div>
			</div>
		`;
	}
	
	handleInput(event) {
		this.value = event.target.value;
		const inputEvent = new CustomEvent("input", { detail: { value: this.value } });
		this.dispatchEvent(inputEvent);
	}
}

export class BigCheckBoxField extends CheckboxFieldBase {
	static className = "BigCheckBoxField";
	
	render() {
	    return html`
	        <div class="row align-items-center">
	            <div class="col-sm-3"></div>
	            <div class="col-sm-9 text-center">
	                <input
	                    type="checkbox"
	                    ?checked="${this.checked}"
	                    id="${this.idInput}"
	                    name="${this.name}"
	                    @change="${this.handleChange}" />
	                <br/>
	                ${this.label}
	            </div>
	        </div>
	    `;
	}
}

export class DateField extends ValueField {
	static className = "DateField";
	
	static properties = {
		...ValueField.properties,
		min: { type: String },
		max: { type: String }
	};
	
	constructor() {
		super();
		this.flatpickrInstance = null;
	}
	
	render() {
		return html`
			<div class="row align-items-center">
				<label for="${this.idInput}" class="col-sm-3 col-form-label text-end">${this.label}</label>
				<div class="col-sm-9">
					<input
						type="text"
						id="${this.idInput}"
						name="${this.name}"
						.value="${this.value ?? ''}"
						data-input
						?required=${this.required}
					/>
				</div>
			</div>
		`;
	}
	
	firstUpdated() {
		this.initializeFlatpickr();
	}
	
	initializeFlatpickr() {
		const input = this.renderRoot.querySelector('input');
		if (!input || this.flatpickrInstance) return;
		
		// Load flatpickr dynamically
		if (typeof flatpickr === 'undefined') {
			console.error('flatpickr is not loaded. Please include flatpickr.min.js in your page.');
			return;
		}
		
		const config = {
			dateFormat: "Y-m-d",
			altInput: true,
			altFormat: "d/m/Y",
			locale: "fr",
			onChange: (selectedDates, dateStr) => {
				// Update the component's value when date changes
				this.value = dateStr;
				this.dispatchChangeEvent();
			}
		};
		
		// Add min/max constraints if provided
		if (this.min) {
			config.minDate = this.min;
		}
		if (this.max) {
			config.maxDate = this.max;
		}
		
		// Initialize flatpickr
		this.flatpickrInstance = flatpickr(input, config);
		
		// Apply same styles to altInput that the original input would have had
		// This ensures visual consistency with other form fields
		if (this.flatpickrInstance.altInput) {
			// Copy all classes from the original input to the altInput
			this.flatpickrInstance.altInput.className = input.className;
			
			// Copy relevant attributes
			if (this.required) {
				this.flatpickrInstance.altInput.setAttribute('required', '');
			}
			if (input.hasAttribute('data-input')) {
				this.flatpickrInstance.altInput.setAttribute('data-input', '');
			}
			
			// Ensure the altInput has the same ID for label association
			// The original input is hidden, so we swap IDs
			if (this.idInput) {
				input.id = `${this.idInput}_hidden`;
				this.flatpickrInstance.altInput.id = this.idInput;
			}
		}
		
		// Set initial value if exists
		if (this.value) {
			this.flatpickrInstance.setDate(this.value, false);
		}
	}
	
	dispatchChangeEvent() {
		this.dispatchEvent(new CustomEvent("change", { 
			detail: { value: this.value }, 
			bubbles: true 
		}));
	}
	
	updated(changedProperties) {
		super.updated(changedProperties);
		
		// Update flatpickr if value changes externally
		if (changedProperties.has('value') && this.flatpickrInstance) {
			const currentPickerValue = this.flatpickrInstance.input.value;
			if (currentPickerValue !== this.value) {
				this.flatpickrInstance.setDate(this.value || null, false);
			}
		}
	}
	
	disconnectedCallback() {
		// Clean up flatpickr instance when component is removed
		if (this.flatpickrInstance) {
			this.flatpickrInstance.destroy();
			this.flatpickrInstance = null;
		}
		super.disconnectedCallback();
	}
	
	getValues() {
		// Get value from flatpickr's hidden input, not the altInput
		if (this.flatpickrInstance && this.flatpickrInstance.input) {
			this.value = this.flatpickrInstance.input.value;
		}
		return { "name": this.name, "value": this.value ?? "", ...super.getValues() };
	}
	
	setValues(values) {
		super.setValues(values);
		if (values.value !== undefined) {
			this.value = values.value;
			// Update flatpickr if it's already initialized
			if (this.flatpickrInstance) {
				this.flatpickrInstance.setDate(values.value || null, false);
			}
		}
	}
}

export class ButtonField extends ShadowDOMElement {
	static className = "ButtonField";
	
	static properties = {
		idInput: { type: String, attribute: "id-input" },
		buttonClass: { type: String },
		onClickHandler: { type: Function, attribute: false },
	};

	constructor() {
		super();
		this.idButton = "";
		this.buttonClass = "btn btn-primary";
		this.onClickHandler = null;
	}

	render() {
		return html`
		<div class="row align-items-center my-3">
			<div class="col-sm-3"></div>
			<div class="col-sm-9">
				<button id="${this.idInput}" class="${this.buttonClass}" @click="${this.handleClick}"><slot></slot></button>
			</div>
		</div>
	    `;
	}

	handleClick (event){
		if (this.onClickHandler && typeof this.onClickHandler === "function")
			this.onClickHandler (event);
	}
}

export class AutocompleteTextField extends ValueField {
	static className = "AutocompleteTextField";
	
	static properties = {
		...ValueField.properties,
		minLength: { type: Number },
		apiUrl: { type: String, attribute: "api-url" },
		onEnterHandler: { type: Function, attribute: "on-enter" }
	};

	constructor() {
		super();
		this.minLength = 3;
		this.onEnterHandler = null;
		this.suggestions = [];
		this.currentIndex = -1;
		this.showDropdown = false;
		this.id = `autocomplete-input-${crypto.randomUUID()}`;
	}

	render() {
    return html`
      <div class="row align-items-center">
        <label for="${this.id}" class="col-sm-3 col-form-label text-end">
          ${this.label}
        </label>
        <div class="col-sm-9 position-relative">
          <input
            id="${this.id}"
            type="text"
			name="${this.name}"
            placeholder="${this.placeholder}"
            @input="${this.handleInput}"
            @keydown="${this.handleKeydown}"
            @blur="${this.handleBlur}"
			autocomplete="new-password"
			value="${this.value}"
			?required="${this.required}"
          />
          ${this.showDropdown && this.suggestions.length > 0
            ? html`
                <ul class="dropdown-menu show">
                  ${this.suggestions.map (
                  	(item, index) => html`<li class="dropdown-item ${index === this.currentIndex ? "active" : ""}" @mousedown="${() => this.handleSelection(item)}">${item.name}</li>`
                  )}
                </ul>
              `
            : ""}
        </div>
      </div>
    `;
  	}

	async handleInput(event) {
		this.value = event.target.value;
		if (this.value.length >= this.minLength) {
			try {
				const separator = this.apiUrl.includes('?') ? '&' : '?';
				const response = await fetch(`${this.apiUrl}${separator}query=${this.value}`);
				this.suggestions = await response.json();
				this.showDropdown = true;
				this.currentIndex = -1;
			} catch (error) {
				console.error("Error fetching suggestions:", error);
				this.suggestions = [];
			}
		} else {
			this.suggestions = [];
			this.showDropdown = false;
		}
		this.requestUpdate();
	}
	
	handleKeydown(event) {
		if (this.suggestions.length > 0) {
			switch (event.key) {
				case "ArrowDown":
					this.currentIndex = (this.currentIndex + 1) % this.suggestions.length;
					break;
				case "ArrowUp":
					this.currentIndex =
						(this.currentIndex - 1 + this.suggestions.length) %
						this.suggestions.length;
					break;
				case "Escape":
					this.showDropdown = false;
					break;
				case "Enter":
					if (this.currentIndex >= 0) {
						this.handleSelection(this.suggestions[this.currentIndex]);
					}
					break;
				default:
					return;
			}
			this.requestUpdate();
		}
	}

	handleBlur() {
		setTimeout(() => {
			this.showDropdown = false;
			this.requestUpdate();
		}, 150);
	}

	handleSelection(item) {
		const input = this.renderRoot.querySelector("input");
		input.value = item.value;
		this.value = input.value;
		this.showDropdown = false;
		this.triggerOnEnter(item);
		this.requestUpdate();
	}

	triggerOnEnter(item) {
		if (this.onEnterHandler)
			try {
				this.onEnterHandler (item);
			} catch (error) {
				console.error("Error executing onEnterHandler:", error);
			}
	}
}

export class TooltipIcon extends ShadowDOMElement {
	static className = "TooltipIcon";
	
	static properties = {
		tooltipText: { type: String, attribute: "tooltip-text" },
		iconUrl: { type: String, attribute: "icon-url" }
	};

	constructor() {
		super();
		this.tooltipText = "Default Tooltip Text";
		this.iconUrl = window.APP_URL.icons.tooltip;
	}

	render() {
		return html`
	<div class="tooltip-icon" data-bs-toggle="tooltip" data-bs-placement="top" title="${this.tooltipText}">
		<img src="${this.iconUrl}" alt="Icon" class="icon-image" />
	</div>
    `;
	}

	firstUpdated() {
		const tooltipTrigger = this.renderRoot.querySelector(".tooltip-icon");
		new bootstrap.Tooltip(tooltipTrigger);
	}
}

export class RowWithTooltip extends ShadowDOMElement {
	static className = "RowWithTooltip";
	
	static properties = {
		tooltipText: { type: String, attribute: "tooltip-text" },
	};

	constructor() {
		super();
		this.tooltipText = "";
	}

	render() {
    return html`
      <div class="row align-items-center">
        <div class="col" style="max-width: calc(100% - 80px);"><slot></slot></div>
        ${this.tooltipText
          ? html`
              <div class="col" style="max-width: 60px; text-align: left;">
                <tooltip-icon tooltip-text="${this.tooltipText}"></tooltip-icon>
              </div>
            `
          : ""}
      </div>
    `;
	}
}

export class RowWrapper extends ShadowDOMElement {
	static className = "RowWrapper";
	
	static properties = {
		visible: {
			type: Boolean, attribute: "visible", reflect: true,
			converter: {
				fromAttribute: (value) => value === "true",
				toAttribute: (value) => (value ? "true" : "false"), 
			}
		}
	};
	
	constructor() {
		super();
		this.visible = true;
	}
	
	render() {
		return html`
		<div class="row align-items-center" style="display: ${this.visible ? 'block' : 'none'}">
			<div class="col" style="max-width: calc(100% - 80px);"><slot></slot></div>
		</div>
	    `;
	}
}

export class DivContainer extends ShadowDOMElement {
	static className = "DivContainer";
	
	static properties = {
		visible: {
			type: Boolean, attribute: "visible", reflect: true,
			converter: {
				fromAttribute: (value) => value === "true",
				toAttribute: (value) => (value ? "true" : "false"), 
			}
		},
		content: { type: Function }
	};

	constructor() {
		super();
		this.visible = true;
		this.content = null;
	}

	render() {
		return html`
			<div style="display: ${this.visible ? 'block' : 'none'};">
				${this.content ? this.content() : ""}
				<slot></slot>
			</div>
		`;
	}
	
	isTransient (){
		return !this.getAttribute ("id");
	}
}

export class CheckBoxField extends CheckboxFieldBase {
	static className = "CheckBoxField";
	
	render() {
	    return html`
	        <div class="row align-items-center">
	            <div class="col-sm-3"></div>
	            <div class="col-sm-9 form-check">
	                <input
	                    class="form-check-input"
	                    type="checkbox"
	                    ?checked="${this.checked}"
	                    id="${this.idInput}"
	                    name="${this.name}"
	                    @change="${this.handleChange}" />
	                <label class="form-check-label" for="${this.idInput}" style="margin: 4px">
	                    ${this.label}
	                </label>
	            </div>
	        </div>
	    `;
	}
}

export class RadioButtonsField extends ValueField {
	static className = "RadioButtonsField";
	
	static properties = {
		...ValueField.properties,
		options: { type: Array },
		onChangeHandler: { type: Function, attribute: "on-change-handler" }
	};

	constructor() {
		super();
		this.options = [];
		this.onChangeHandler = null;
	}

	render() {
    return html`
      <div class="row align-items-center">
        <div class="col-sm-3"></div>
        <div class="col-sm-9">
          ${this.options.map(
            (option) => html`
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="${this.name}"
                  value="${option.value}"
                  ?checked="${this.value === option.value}"
                  @change="${this.handleChange}"
                />
                <label class="form-check-label m-1">${option.title}</label>
              </div>
            `
          )}
        </div>
      </div>
    `;
	}

	handleChange(event) {
		this.value = event.target.value;
		if (this.onChangeHandler) {
			this.onChangeHandler(event.target.value);
		}
		const changeEvent = new CustomEvent("change", { detail: { value: this.value } });
		this.dispatchEvent(changeEvent);
	}
}

/**
 * Specialized text field for RCS numbers (Registre du Commerce et des Sociétés).
 * Derives from TextField with predefined label and RCS pattern validation.
 * 
 * Attributes:
 *   - enforce-pattern (Boolean): When true, applies the RCS regex pattern. Default: true.
 *   - label: Defaults to "Numéro de RCS" (can be overridden, e.g. "Numéro de RCS *").
 *   - name: Defaults to "registration_number".
 */
export class RcsTextField extends TextField {
	static className = "RcsTextField";
	
	static RCS_PATTERN = "[A-Z]?\\s?(\\d{9}|\\d{3}\\s\\d{3}\\s\\d{3})";
	
	static properties = {
		...TextField.properties,
		enforcePattern: { type: Boolean, attribute: "enforce-pattern" }
	};
	
	constructor() {
		super();
		this.label = "Numéro de RCS";
		this.name = "registration_number";
		this.enforcePattern = true;
	}
	
	updated(changedProperties) {
		super.updated?.(changedProperties);
		if (changedProperties.has('enforcePattern')) {
			this.pattern = this.enforcePattern ? RcsTextField.RCS_PATTERN : undefined;
		}
	}
}

/**
 * Wrapper component that combines an RcsTextField inside a row-with-tooltip.
 * Provides the standard RCS tooltip and pattern enforcement.
 * 
 * Attributes:
 *   - show-tooltip (Boolean): Show/hide the RCS tooltip. Default: true.
 *   - enforce-pattern (Boolean): When true, applies the RCS regex pattern. Default: true.
 *   - label: Label for the inner RcsTextField. Default: "Numéro de RCS".
 *   - name: Name for the inner RcsTextField. Default: "registration_number".
 *   - required (Boolean): Whether the field is required.
 */
export class RcsNumberField extends ShadowDOMElement {
	static className = "RcsNumberField";
	
	static RCS_TOOLTIP = "Le numéro de RCS est composé de 9 chiffres (par exemple : 123 456 789)";
	
	static properties = {
		showTooltip: { type: Boolean, attribute: "show-tooltip" },
		enforcePattern: { type: Boolean, attribute: "enforce-pattern" },
		label: { type: String },
		name: { type: String },
		required: { type: Boolean },
		value: { type: String }
	};
	
	constructor() {
		super();
		this.showTooltip = true;
		this.enforcePattern = true;
		this.label = "Numéro de RCS";
		this.name = "registration_number";
		this.required = false;
		this.value = "";
	}
	
	render() {
		const textField = html`
			<rcs-text-field
				label="${this.label}"
				name="${this.name}"
				?required="${this.required}"
				?enforce-pattern="${this.enforcePattern}"
				.value="${this.value}"
			></rcs-text-field>
		`;
		
		if (this.showTooltip) {
			return html`
				<row-with-tooltip tooltip-text="${RcsNumberField.RCS_TOOLTIP}">
					${textField}
				</row-with-tooltip>
			`;
		}
		
		return html`<row-wrapper>${textField}</row-wrapper>`;
	}
	
	getValues() {
		const inner = this.shadowRoot?.querySelector('rcs-text-field');
		if (inner) return inner.getValues();
		return { "name": this.name, "value": this.value ?? "", ...super.getValues() };
	}
	
	setValues(values) {
		super.setValues(values);
		if (values.value !== undefined) {
			this.value = values.value;
			const inner = this.shadowRoot?.querySelector('rcs-text-field');
			if (inner) inner.setValues(values);
		}
	}
	
	isTransient() {
		return false;
	}
}

customElements.define("number-field", NumberField);
customElements.define("text-field", TextField);
customElements.define("rcs-text-field", RcsTextField);
customElements.define("rcs-number-field", RcsNumberField);
customElements.define("subtitle-field", SubtitleField);
customElements.define("title-field", TitleField);
customElements.define("option-component", OptionComponent);
customElements.define("select-field", SelectField);
customElements.define("text-area-field", TextAreaField);
customElements.define("big-checkbox-field", BigCheckBoxField);
customElements.define("date-field", DateField);
customElements.define("button-field", ButtonField);
customElements.define("autocomplete-text-field", AutocompleteTextField);
customElements.define("tooltip-icon", TooltipIcon);
customElements.define("row-with-tooltip", RowWithTooltip);
customElements.define("row-wrapper", RowWrapper);
customElements.define("div-container", DivContainer);
customElements.define("checkbox-field", CheckBoxField);
customElements.define("radio-buttons-field", RadioButtonsField);
